import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Card, Typography, Button, Input, Space, Tag, Tabs, Table, message, Row, Col,
  Statistic, Empty, Alert, AutoComplete, Tooltip, Checkbox, Popconfirm,
} from 'antd';
import {
  LinkOutlined, SearchOutlined, CopyOutlined, DownloadOutlined, ReloadOutlined,
  AudioOutlined, ExportOutlined, DeleteOutlined, CheckCircleOutlined, MailOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import api from '../utils/api';
import { downloadCsv } from '../utils/exportCsv';
import useVoiceInput from '../utils/voice';
import ShortLinks from './ShortLinks';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const matchTag = (m, conf) => {
  if (m === 'isrc') return <Tag color="green">Exact (ISRC)</Tag>;
  if (m === 'upc') return <Tag color="green">Exact (UPC)</Tag>;
  if (m === 'exact') return <Tag color="cyan">Exact match</Tag>;
  if (m === 'name') return <Tag color="blue">Name + artist verified</Tag>;
  if (m === 'title') return <Tag color="orange">{`Title match ${conf}%`}</Tag>;
  if (m === 'manual') return <Tag color="purple">Aapka diya link</Tag>;
  return <Tag>Search only</Tag>;
};

// song_metadata.extra_store_links = {"Wynk": "https://..."} -> [{name, url}]
function parseExtra(raw) {
  if (!raw) return [];
  try {
    const obj = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!obj || typeof obj !== 'object') return [];
    return Object.keys(obj).map((name) => ({ name, url: obj[name] }));
  } catch (err) {
    return [];
  }
}

function copy(text, what = 'Link') {
  if (!text) return;
  navigator.clipboard.writeText(text)
    .then(() => message.success(`${what} copied`))
    .catch(() => message.error('Copy nahi hua — link manually copy karein'));
}

// Ready-made block (♪Amazon : link ...) — ek click me poora copy, YouTube description ke liye.
function CopyBlock({ res, loading }) {
  const text = res.copy_text || res.copy_text_full || '';
  if (loading) return <Card size="small" loading style={{ marginBottom: 12 }} />;
  if (!text) return null;
  const copyOne = (full) => {
    const t = full ? (res.copy_text_full || res.copy_text) : (res.copy_text || res.copy_text_full);
    copy(t, full ? 'Poore links' : 'Saare links');
  };
  return (
    <Card
      size="small"
      style={{ marginBottom: 12, background: '#fafafa' }}
      title="Copy karne wala block (YouTube description ke liye)"
      extra={(
        <Space wrap>
          <Button type="primary" size="small" icon={<CopyOutlined />} onClick={() => copyOne(false)}>Copy all (short)</Button>
          {res.copy_text_full ? (
            <Button size="small" icon={<CopyOutlined />} onClick={() => copyOne(true)}>Copy full</Button>
          ) : null}
        </Space>
      )}
    >
      <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 13, fontFamily: 'monospace' }}>{text}</pre>
    </Card>
  );
}

function PlatformTable({ result }) {
  const extras = (result.extras || []).map((e, i) => ({
    ...e, match: 'manual', color: '#722ed1', rowKey: `extra-${i}`,
  }));
  const rows = extras.concat((result.platforms || []).map((p, i) => ({ ...p, rowKey: `${p.key}-${i}` })));
  const columns = [
    {
      title: 'Store / Platform',
      dataIndex: 'name',
      width: 200,
      render: (name, r) => (
        <Space>
          <span style={{ width: 10, height: 10, borderRadius: 10, background: r.color, display: 'inline-block' }} />
          <b>{name}</b>
        </Space>
      ),
    },
    {
      title: 'Match',
      dataIndex: 'match',
      width: 160,
      render: (m, r) => matchTag(m, r.confidence),
    },
    {
      title: 'Link',
      dataIndex: 'url',
      render: (url, r) => (!url ? <Text type="secondary">—</Text> : (
        <div>
          <a href={url} target="_blank" rel="noopener noreferrer">
            {r.match === 'search' ? 'Search on ' + r.name : url}
          </a>
          {r.short_url ? (
            <div style={{ fontSize: 12 }}>
              <Text type="secondary">Short: </Text>
              <a href={r.short_url} target="_blank" rel="noopener noreferrer">{r.short_url}</a>
            </div>
          ) : null}
        </div>
      )),
    },
    {
      title: '',
      width: 150,
      render: (_, r) => (r.url ? (
        <Space>
          <Tooltip title="Copy link"><Button size="small" icon={<CopyOutlined />} onClick={() => copy(r.url, r.name + ' link')} /></Tooltip>
          {r.short_url && (
            <Tooltip title="Copy short link">
              <Button size="small" icon={<CopyOutlined />} onClick={() => copy(r.short_url, r.name + ' short link')}>S</Button>
            </Tooltip>
          )}
          <Tooltip title="Open"><Button size="small" icon={<ExportOutlined />} onClick={() => window.open(r.url, '_blank', 'noopener,noreferrer')} /></Tooltip>
        </Space>
      ) : null),
    },
  ];
  return (
    <Table
      size="small"
      rowKey="rowKey"
      dataSource={rows}
      columns={columns}
      pagination={false}
      rowClassName={(r) => (r.match === 'search' ? 'sl-row-search' : '')}
    />
  );
}

export default function StoreLinks({ user }) {
  const isAdmin = ['admin', 'super_admin', 'manager'].includes(user?.role);

  // Song Metadata se aaya link: ?title=&singer=&isrc=&upc= — turant search ho jata hai.
  const query = new URLSearchParams(useLocation().search);
  const [title, setTitle] = useState(query.get('title') || '');
  const [singer, setSinger] = useState(query.get('singer') || '');
  const [isrc, setIsrc] = useState(query.get('isrc') || '');
  const [upc, setUpc] = useState(query.get('upc') || '');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [exactOnly, setExactOnly] = useState(true);
  const [reelsUrl, setReelsUrl] = useState('');
  const [intubeUrl, setIntubeUrl] = useState('');
  const [extraLinks, setExtraLinks] = useState([]);
  const [metadataId, setMetadataId] = useState(Number(query.get('metadata_id')) || 0);
  const [result, setResult] = useState(null);
  const [mailing, setMailing] = useState(false);

  const [bulkText, setBulkText] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkRows, setBulkRows] = useState([]);

  const [saved, setSaved] = useState([]);
  const [savedDetail, setSavedDetail] = useState({});
  const [expandedSaved, setExpandedSaved] = useState([]);
  const [savedQuery, setSavedQuery] = useState('');
  const [savedLoading, setSavedLoading] = useState(false);

  const [catalog, setCatalog] = useState([]);
  const [catalogTotal, setCatalogTotal] = useState(0);
  const [catalogPage, setCatalogPage] = useState(1);
  const [catalogQuery, setCatalogQuery] = useState('');
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const t = query.get('tab');
    return ['single', 'catalog', 'bulk', 'saved', 'short'].includes(t) ? t : 'single';
  });
  const [autoFilled, setAutoFilled] = useState(null);
  useEffect(() => {
    if (activeTab === 'saved') loadSaved();
    if (activeTab === 'catalog' && !catalog.length) loadCatalog(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const voice = useVoiceInput((text) => setTitle(text));
  const autoRan = useRef(false);

  const lookupRef = useRef(null);
  useEffect(() => {
    if (autoRan.current) return;
    if (!(query.get('title') || query.get('isrc') || query.get('upc'))) return;
    autoRan.current = true;
    if (lookupRef.current) lookupRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchSongs = useCallback(async (q) => {
    if (!q || q.length < 2) { setOptions([]); return; }
    try {
      const res = await api.get('/store-links/songs', { params: { q } });
      setOptions((res.data || []).map((s) => ({
        value: s.title,
        label: (
          <div>
            <b>{s.title}</b>
            <div style={{ fontSize: 12, color: '#888' }}>
              {[s.singer, s.isrc && `ISRC ${s.isrc}`, s.upc && `UPC ${s.upc}`].filter(Boolean).join(' · ')}
            </div>
          </div>
        ),
        song: s,
      })));
    } catch (err) { /* autocomplete failure is not fatal */ }
  }, []);

  const fillFromSong = (s) => {
    setTitle(s.title || '');
    setSinger(s.singer || '');
    setIsrc(s.isrc || '');
    setUpc(s.upc || '');
    setMetadataId(s.id || 0);
    setReelsUrl(s.reels_link || '');
    setIntubeUrl(s.intube_link || '');
    setExtraLinks(parseExtra(s.extra_store_links));
    setAutoFilled(s.title || '');
  };

  const pickSong = (_, option) => fillFromSong(option.song || {});

  // Title type karte hi (bina dropdown se chune bhi) Song Metadata se singer/ISRC/UPC bhar deta hai.
  const norm = (s) => (s || '').toLowerCase().replace(/[^a-z0-9\u0900-\u097f]+/g, ' ').trim();
  const autoFillFromMetadata = async (t, allowSingle = false) => {
    const q = (t || '').trim();
    if (q.length < 3 || metadataId) return null;
    try {
      const res = await api.get('/store-links/songs', { params: { q } });
      const rows = res.data || [];
      const exact = rows.find((s) => norm(s.title) === norm(q)) || (allowSingle && rows.length === 1 ? rows[0] : null);
      if (exact) { fillFromSong(exact); return exact; }
    } catch (err) { /* ignore */ }
    return null;
  };

  useEffect(() => {
    if (metadataId) return undefined;
    const t = setTimeout(() => { autoFillFromMetadata(title); }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title]);

  const lookup = async (override) => {
    let t = title, sg = singer, ic = isrc, uc = upc, mid = metadataId;
    let reels = reelsUrl, intube = intubeUrl, extras = extraLinks;
    if (override && override.title !== undefined) {
      t = override.title || ''; sg = override.singer || ''; ic = override.isrc || ''; uc = override.upc || ''; mid = override.id || 0;
      reels = override.reels_link || ''; intube = override.intube_link || ''; extras = parseExtra(override.extra_store_links);
    } else if (!ic.trim() && !uc.trim() && t.trim()) {
      const s = await autoFillFromMetadata(t, true);
      if (s) {
        t = s.title || t; sg = s.singer || sg; ic = s.isrc || ''; uc = s.upc || ''; mid = s.id || 0;
        reels = s.reels_link || reels; intube = s.intube_link || intube;
        const ex = parseExtra(s.extra_store_links); if (ex.length) extras = ex;
      }
    }
    if (!t.trim() && !ic.trim() && !uc.trim()) {
      return message.warning('Gaane ka naam, ya ISRC, ya UPC daalein');
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await api.post('/store-links/lookup', {
        title: t.trim(),
        singer: (sg || '').trim(),
        isrc: (ic || '').trim(),
        upc: (uc || '').trim(),
        refresh,
        exact_only: exactOnly,
        reels_url: (reels || '').trim(),
        intube_url: (intube || '').trim(),
        extra_links: extras
          .map((r) => ({ name: (r.name || '').trim(), url: (r.url || '').trim() }))
          .filter((r) => r.name && r.url),
        metadata_id: mid || undefined,
      });
      setResult(res.data);
      if (res.data.reels_url) setReelsUrl(res.data.reels_url);
      if (res.data.intube_url) setIntubeUrl(res.data.intube_url);
      if (Array.isArray(res.data.extra_links)) setExtraLinks(res.data.extra_links);
      if (!res.data.found) message.warning('Kisi bhi store par ye gaana nahi mila — title/ISRC dobara check karein');
    } catch (err) {
      message.error(err.uiMessage || 'Links nahi mile');
    } finally {
      setLoading(false);
    }
  };
  lookupRef.current = lookup;

  // Song Data tab se: row ka data bharo, "Ek Gaana" tab par jao aur turant links nikalo.
  const findFromCatalog = (s) => {
    fillFromSong(s);
    setActiveTab('single');
    lookup(s);
  };

  const loadCatalog = async (page = 1, q = catalogQuery) => {
    setCatalogLoading(true);
    try {
      const res = await api.get('/store-links/catalog', { params: { q, page, limit: 50 } });
      setCatalog(res.data.rows || []);
      setCatalogTotal(res.data.total || 0);
      setCatalogPage(page);
    } catch (err) {
      message.error(err.uiMessage || 'Song data nahi aaya');
    } finally {
      setCatalogLoading(false);
    }
  };

  const clearForm = () => {
    setTitle(''); setSinger(''); setIsrc(''); setUpc(''); setMetadataId(0);
    setReelsUrl(''); setIntubeUrl(''); setExtraLinks([]); setResult(null); setAutoFilled(null);
  };

  // Is gaane ke links (copy block ke saath) admin ko mail kar do.
  const sendMail = async () => {
    setMailing(true);
    try {
      const res = await api.post('/store-links/mail', {
        title: title.trim(),
        singer: singer.trim(),
        isrc: isrc.trim(),
        upc: upc.trim(),
        metadata_id: metadataId || undefined,
      });
      message.success(`Mail bhej di — ${res.data.to}`);
    } catch (err) {
      message.error(err.uiMessage || 'Mail nahi gayi');
    } finally {
      setMailing(false);
    }
  };

  // Saved row se seedha mail (us gaane ke exact links + short links).
  const mailSaved = async (r) => {
    try {
      const res = await api.post('/store-links/mail', {
        title: r.title || '',
        singer: r.singer || '',
        isrc: r.isrc || '',
        upc: r.upc || '',
        metadata_id: r.metadata_id || undefined,
      });
      message.success(`Mail bhej di — ${res.data.to}`);
    } catch (err) {
      message.error(err.uiMessage || 'Mail nahi gayi');
    }
  };

  const runBulk = async () => {
    const text = bulkText.trim();
    if (!text) return message.warning('Har line me ek gaana likho: Title | Singer | ISRC | UPC');
    setBulkLoading(true);
    try {
      const res = await api.post('/store-links/bulk', { text, refresh, exact_only: exactOnly });
      setBulkRows(res.data.results || []);
      message.success(`${res.data.count} gaane check ho gaye`);
    } catch (err) {
      message.error(err.uiMessage || 'Bulk search fail');
    } finally {
      setBulkLoading(false);
    }
  };

  const loadSaved = async () => {
    setSavedLoading(true);
    try {
      const res = await api.get('/store-links/saved', { params: { q: savedQuery } });
      setSaved(res.data || []);
    } catch (err) {
      message.error(err.uiMessage || 'List nahi aayi');
    } finally {
      setSavedLoading(false);
    }
  };

  // Saved row kholne par uska poora copy block (short links ke saath) laata hai.
  const loadSavedDetail = async (id) => {
    if (savedDetail[id]) return;
    try {
      const res = await api.get(`/store-links/saved/${id}`);
      setSavedDetail((prev) => ({ ...prev, [id]: res.data }));
    } catch (err) {
      message.error(err.uiMessage || 'Links nahi aaye');
    }
  };

  const deleteSaved = async (id) => {
    try {
      await api.delete(`/store-links/saved/${id}`);
      message.success('Hata diya — agli baar dobara search hoga');
      loadSaved();
    } catch (err) {
      message.error(err.uiMessage || 'Delete fail');
    }
  };

  // Ready-made block (♪Amazon : short link ...) — server hi banata hai.
  const copyAll = (res, full = false) => {
    const text = full ? (res.copy_text_full || res.copy_text) : (res.copy_text || res.copy_text_full);
    if (text) return copy(text, full ? 'Poore links' : 'Saare links');
    const lines = (res.platforms || [])
      .filter((p) => p.match !== 'search' && p.url)
      .map((p) => `♪${p.short || p.name} : ${p.url}`);
    if (!lines.length) return message.warning('Koi asli link nahi mila');
    copy(`${res.title || ''}\n\n${lines.join('\n')}`, 'Saare links');
  };

  const foundCols = [
    { title: 'No.', width: 60, render: (_, __, i) => i + 1 },
    { title: 'Song', dataIndex: 'title', render: (t, r) => <div><b>{t || '—'}</b>{r.singer ? <div style={{ fontSize: 12, color: '#888' }}>{r.singer}</div> : null}</div> },
    { title: 'ISRC', dataIndex: 'isrc', width: 140 },
    { title: 'UPC', dataIndex: 'upc', width: 140 },
    { title: 'Links mile', dataIndex: 'found', width: 110, render: (v, r) => <Space><Tag color={v ? 'blue' : 'default'}>{v}</Tag>{r.exact ? <Tag color="green">{r.exact} exact</Tag> : null}</Space> },
    {
      title: '',
      width: 120,
      render: (_, r) => <Button size="small" icon={<CopyOutlined />} onClick={() => copyAll(r)}>Copy</Button>,
    },
  ];

  const savedCols = [
    { title: 'No.', width: 60, render: (_, __, i) => i + 1 },
    { title: 'Song', dataIndex: 'title', render: (t, r) => <div><b>{t || '—'}</b>{r.singer ? <div style={{ fontSize: 12, color: '#888' }}>{r.singer}</div> : null}</div> },
    { title: 'ISRC', dataIndex: 'isrc', width: 140 },
    { title: 'UPC', dataIndex: 'upc', width: 140 },
    { title: 'Links', dataIndex: 'found', width: 110, render: (v, r) => <Space><Tag color={v ? 'blue' : 'default'}>{v}</Tag>{r.exact ? <Tag color="green">{r.exact} exact</Tag> : null}</Space> },
    { title: 'Checked', dataIndex: 'updated_at', width: 170 },
    {
      title: '',
      width: 180,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<CopyOutlined />} onClick={() => copyAll(r)} />
          <Tooltip title="Is gaane ke links mail karo">
            <Button size="small" icon={<MailOutlined />} onClick={() => mailSaved(r)} />
          </Tooltip>
          {isAdmin && (
            <Popconfirm title="Ye result hata dein? (agli baar dobara search hoga)" onConfirm={() => deleteSaved(r.id)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const catalogCols = [
    { title: 'No.', width: 60, render: (_, __, i) => (catalogPage - 1) * 50 + i + 1 },
    { title: 'Song Title', dataIndex: 'title', render: (t, r) => <div><b>{t || '—'}</b>{r.label ? <div style={{ fontSize: 12, color: '#888' }}>{r.label}</div> : null}</div> },
    { title: 'Singer', dataIndex: 'singer', width: 180, render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'ISRC', dataIndex: 'isrc', width: 150, render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'UPC', dataIndex: 'upc', width: 150, render: (v) => v || <Text type="secondary">—</Text> },
    {
      title: 'Store Links',
      width: 150,
      render: (_, r) => (r.store_link_id
        ? <Space size={4}><Tag color="blue">{r.found_count} links</Tag>{Number(r.exact_count) > 0 ? <Tag color="green">{r.exact_count} exact</Tag> : null}</Space>
        : <Tag>Abhi nahi nikale</Tag>),
    },
    {
      title: '',
      width: 170,
      render: (_, r) => (
        <Button type="primary" size="small" icon={<SearchOutlined />} onClick={() => findFromCatalog(r)}>
          Find Store Links
        </Button>
      ),
    },
  ];

  return (
    <div>
      <style>{`.sl-row-search td { opacity: .55 }`}</style>
      <Title level={3} style={{ marginBottom: 4 }}><LinkOutlined /> Store Links</Title>
      <Text type="secondary">
        Gaane ka ISRC / UPC (ya naam) daalo — Spotify, Apple Music, JioSaavn, YouTube Music, Amazon, Deezer, Tidal sab ke link mil jayenge.
      </Text>

      <Tabs
        activeKey={activeTab}
        style={{ marginTop: 16 }}
        onChange={(k) => {
          setActiveTab(k);
          if (k === 'saved') loadSaved();
          if (k === 'catalog' && !catalog.length) loadCatalog(1);
        }}
        items={[
          {
            key: 'single',
            label: 'Ek Gaana',
            children: (
              <>
                <Card>
                  <Row gutter={[12, 12]}>
                    <Col xs={24} md={10}>
                      <Text strong>Gaane ka naam</Text>
                      <Space.Compact style={{ width: '100%', marginTop: 4 }}>
                        <AutoComplete
                          style={{ width: '100%' }}
                          value={title}
                          options={options}
                          onSearch={(v) => { setTitle(v); setMetadataId(0); setAutoFilled(null); searchSongs(v); }}
                          onChange={setTitle}
                          onSelect={pickSong}
                          placeholder="jaise: Tope Kalakand Burset (Song Metadata se suggest hoga)"
                        />
                        {voice.supported && (
                          <Tooltip title="Bol ke likho">
                            <Button
                              icon={<AudioOutlined />}
                              danger={voice.listening}
                              onClick={voice.toggle}
                            />
                          </Tooltip>
                        )}
                      </Space.Compact>
                    </Col>
                    {autoFilled ? (
                      <Col xs={24}>
                        <Alert
                          type="success"
                          showIcon
                          style={{ padding: '4px 12px' }}
                          message={<span>Song Data se apne aap bhar gaya: <b>{autoFilled}</b>{singer ? ` · ${singer}` : ''}{isrc ? ` · ISRC ${isrc}` : ''}{upc ? ` · UPC ${upc}` : ''} — ab "Find Store Links" dabao.</span>}
                        />
                      </Col>
                    ) : null}
                    <Col xs={24} md={6}>
                      <Text strong>Singer (optional)</Text>
                      <Input style={{ marginTop: 4 }} value={singer} onChange={(e) => setSinger(e.target.value)} placeholder="DG Mawai" />
                    </Col>
                    <Col xs={12} md={4}>
                      <Text strong>ISRC</Text>
                      <Input style={{ marginTop: 4 }} value={isrc} onChange={(e) => setIsrc(e.target.value)} placeholder="DG-A05-25-20449" />
                    </Col>
                    <Col xs={12} md={4}>
                      <Text strong>UPC</Text>
                      <Input style={{ marginTop: 4 }} value={upc} onChange={(e) => setUpc(e.target.value)} placeholder="3618020973005" />
                    </Col>
                    <Col xs={24} md={10}>
                      <Text strong>Instagram Reels audio link (ek baar)</Text>
                      <Input
                        style={{ marginTop: 4 }}
                        value={reelsUrl}
                        onChange={(e) => setReelsUrl(e.target.value)}
                        placeholder="https://www.instagram.com/reels/audio/..."
                      />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Instagram par reel kholo → audio par tap → us page ka link paste karo. Ek baar daalo, gaane ke saath
                        save ho jayega — aage apne aap copy block me aayega.
                      </Text>
                    </Col>
                    <Col xs={24} md={10}>
                      <Text strong>Intube Music link (ek baar)</Text>
                      <Input
                        style={{ marginTop: 4 }}
                        value={intubeUrl}
                        onChange={(e) => setIntubeUrl(e.target.value)}
                        placeholder="https://intubemusic.com/..."
                      />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Intube Music par us gaane ka page kholo aur link paste karo — gaane ke saath save ho jayega.
                      </Text>
                    </Col>
                    <Col xs={24}>
                      <Text strong>Koi aur store ka link (jitne chahe)</Text>
                      <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                        Jo store apne aap nahi mila (Wynk, Hungama, Resso… kuch bhi) uska naam + link daal do — gaane ke
                        saath save ho jayega aur copy block/mail me short link bankar aayega.
                      </Text>
                      {extraLinks.map((row, i) => (
                        <Space key={i} style={{ marginTop: 8, width: '100%' }} align="start" wrap>
                          <Input
                            style={{ width: 160 }}
                            placeholder="Store ka naam"
                            value={row.name}
                            onChange={(e) => setExtraLinks(extraLinks.map((r, j) => (j === i ? { ...r, name: e.target.value } : r)))}
                          />
                          <Input
                            style={{ width: 420 }}
                            placeholder="https://..."
                            value={row.url}
                            onChange={(e) => setExtraLinks(extraLinks.map((r, j) => (j === i ? { ...r, url: e.target.value } : r)))}
                          />
                          <Button
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => setExtraLinks(extraLinks.filter((_, j) => j !== i))}
                          />
                        </Space>
                      ))}
                      <div style={{ marginTop: 8 }}>
                        <Button size="small" icon={<PlusOutlined />} onClick={() => setExtraLinks([...extraLinks, { name: '', url: '' }])}>
                          Store add karo
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  <Space style={{ marginTop: 16 }} wrap>
                    <Button type="primary" size="large" icon={<SearchOutlined />} loading={loading} onClick={() => lookup()}>
                      Find Store Links
                    </Button>
                    <Checkbox checked={exactOnly} onChange={(e) => setExactOnly(e.target.checked)}>
                      Sirf exact match (ISRC/UPC se pakka gaana)
                    </Checkbox>
                    <Checkbox checked={refresh} onChange={(e) => setRefresh(e.target.checked)}>
                      Dobara fresh check karo (cache na use karo)
                    </Checkbox>
                    <Button icon={<ReloadOutlined />} onClick={clearForm}>
                      Clear
                    </Button>
                  </Space>
                  <Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0, fontSize: 12 }}>
                    <b>Sirf exact match</b> chalu hai — sirf ISRC/UPC se pakka hua gaana aata hai, isliye dusri company ka
                    same-naam gaana kabhi nahi aayega (Gaana/JioSaavn ke page naam + artist/label verify karke hi aate hain).
                    Ye tick hatao to naam se bhi dhoondhega, par tab "Title match %" dekh lena. Jo store par gaana nahi mila
                    wahan sirf search page dikhega — wo copy/Excel me nahi jata.
                    Copy karne par links <b>bainslamusic.com/s/...</b> wale chhote (short) link ban ke aate hain.
                  </Paragraph>
                </Card>

                {result && (
                  <Card
                    style={{ marginTop: 16 }}
                    title={(
                      <Space wrap>
                        <b>{result.title || 'Result'}</b>
                        {result.singer ? <Text type="secondary">{result.singer}</Text> : null}
                        {result.cached ? <Tag>saved result</Tag> : <Tag color="blue">fresh</Tag>}
                      </Space>
                    )}
                    extra={(
                      <Space wrap>
                        <Button type="primary" icon={<CopyOutlined />} onClick={() => copyAll(result)}>Copy all (short links)</Button>
                        <Button icon={<CopyOutlined />} onClick={() => copyAll(result, true)}>Copy full links</Button>
                        <Button icon={<MailOutlined />} loading={mailing} onClick={sendMail}>Mail bhejo</Button>
                        <Button icon={<DownloadOutlined />} onClick={() => downloadCsv('/store-links/export', { ids: result.id || '', q: result.id ? '' : (result.title || '') })}>
                          Download Excel
                        </Button>
                      </Space>
                    )}
                  >
                    <Row gutter={16} style={{ marginBottom: 12 }}>
                      <Col xs={12} sm={8}><Statistic title="Links mile" value={result.found} prefix={<CheckCircleOutlined />} /></Col>
                      <Col xs={12} sm={8}><Statistic title="Exact (ISRC/UPC)" value={result.exact} valueStyle={{ color: '#52c41a' }} /></Col>
                      <Col xs={24} sm={8}><Statistic title="Checked" value={result.checked_at || '-'} valueStyle={{ fontSize: 16 }} /></Col>
                    </Row>
                    {(result.notes || []).map((n, i) => (
                      <Alert key={i} type="info" showIcon style={{ marginBottom: 8 }} message={n} />
                    ))}
                    <CopyBlock res={result} />
                    <PlatformTable result={result} />
                  </Card>
                )}
              </>
            ),
          },
          {
            key: 'catalog',
            label: `Song Data${catalogTotal ? ` (${catalogTotal})` : ''}`,
            children: (
              <Card>
                <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
                  Ye Song Metadata / Song Library ka poora data hai (title, singer, ISRC, UPC) — jo bhi naya gaana wahan add hota hai,
                  yahan apne aap aa jata hai. Kisi bhi gaane ke saamne <b>Find Store Links</b> dabao — sab details bhar kar turant links nikal aayenge.
                </Paragraph>
                <Space wrap style={{ marginBottom: 12 }}>
                  <Input
                    placeholder="Song title / singer / ISRC / UPC search"
                    value={catalogQuery}
                    onChange={(e) => setCatalogQuery(e.target.value)}
                    onPressEnter={() => loadCatalog(1)}
                    style={{ width: 320 }}
                    allowClear
                  />
                  <Button icon={<SearchOutlined />} onClick={() => loadCatalog(1)}>Search</Button>
                  <Button icon={<ReloadOutlined />} onClick={() => { setCatalogQuery(''); loadCatalog(1, ''); }}>Refresh</Button>
                </Space>
                <Table
                  size="small"
                  rowKey="id"
                  loading={catalogLoading}
                  dataSource={catalog}
                  columns={catalogCols}
                  pagination={{
                    current: catalogPage,
                    pageSize: 50,
                    total: catalogTotal,
                    showSizeChanger: false,
                    onChange: (p) => loadCatalog(p),
                    showTotal: (t) => `${t} gaane`,
                  }}
                />
              </Card>
            ),
          },
          {
            key: 'bulk',
            label: 'Bahut Saare (Bulk)',
            children: (
              <>
                <Card>
                  <Text strong>Har line me ek gaana</Text>
                  <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
                    Format: <code>Title | Singer | ISRC | UPC</code> — jo ho wahi likh do (sirf ISRC bhi chalega).
                    Ek baar me 25 gaane.
                  </Paragraph>
                  <TextArea
                    rows={8}
                    value={bulkText}
                    onChange={(e) => setBulkText(e.target.value)}
                    placeholder={'Tope Kalakand Burset | DG Mawai | DG-A05-25-20449 | 3618020973005\nDhokho Khave Ko Jo Mil Jato Award | DG Mawai | DG-A05-25-34200'}
                  />
                  <Space style={{ marginTop: 12 }} wrap>
                    <Button type="primary" icon={<SearchOutlined />} loading={bulkLoading} onClick={runBulk}>
                      Sab ke links nikalo
                    </Button>
                    <Checkbox checked={exactOnly} onChange={(e) => setExactOnly(e.target.checked)}>Sirf exact match</Checkbox>
                    <Checkbox checked={refresh} onChange={(e) => setRefresh(e.target.checked)}>Fresh check</Checkbox>
                    {bulkRows.length > 0 && (
                      <Button icon={<DownloadOutlined />} onClick={() => downloadCsv('/store-links/export', { ids: bulkRows.map((r) => r.id).filter(Boolean).join(',') })}>
                        Download Excel (saare)
                      </Button>
                    )}
                  </Space>
                </Card>

                {bulkRows.length > 0 && (
                  <Card style={{ marginTop: 16 }}>
                    <Table
                      size="small"
                      rowKey={(r, i) => `${r.isrc || r.title}-${i}`}
                      dataSource={bulkRows}
                      columns={foundCols}
                      pagination={false}
                      expandable={{
                        expandedRowRender: (r) => (
                          <>
                            <CopyBlock res={r} />
                            <PlatformTable result={r} />
                          </>
                        ),
                      }}
                    />
                  </Card>
                )}
              </>
            ),
          },
          {
            key: 'saved',
            label: 'Saved Links',
            children: (
              <Card>
                <Space wrap style={{ marginBottom: 12 }}>
                  <Input
                    placeholder="Song / ISRC / UPC search"
                    value={savedQuery}
                    onChange={(e) => setSavedQuery(e.target.value)}
                    onPressEnter={loadSaved}
                    style={{ width: 260 }}
                  />
                  <Button icon={<SearchOutlined />} onClick={loadSaved}>Search</Button>
                  <Button icon={<ReloadOutlined />} onClick={() => { setSavedQuery(''); loadSaved(); }}>Refresh</Button>
                  <Button icon={<DownloadOutlined />} onClick={() => downloadCsv('/store-links/export', { q: savedQuery })}>
                    Download Excel
                  </Button>
                </Space>
                {saved.length === 0 ? (
                  <Empty description="Abhi koi saved result nahi — pehle koi gaana search karo" />
                ) : (
                  <Table
                    size="small"
                    rowKey="id"
                    loading={savedLoading}
                    dataSource={saved}
                    columns={savedCols}
                    pagination={{ pageSize: 20, showSizeChanger: false }}
                    expandable={{
                      expandedRowKeys: expandedSaved,
                      onExpand: (open, r) => {
                        setExpandedSaved(open ? [r.id] : []);
                        if (open) loadSavedDetail(r.id);
                      },
                      expandedRowRender: (r) => {
                        const full = savedDetail[r.id];
                        return (
                          <>
                            <CopyBlock res={full || r} loading={!full} />
                            <PlatformTable result={full || r} />
                          </>
                        );
                      },
                    }}
                  />
                )}
              </Card>
            ),
          },
          {
            key: 'short',
            label: 'Short Links',
            children: <ShortLinks user={user} embedded />,
          },
        ]}
      />
    </div>
  );
}
